import { apiClient, apiOrigin, type ApiResult, type RequestOptions } from '@/services/apiClient';

/**
 * M15 analytics reads.
 *
 * Only the two aggregates that could not be composed from an existing endpoint
 * live here. Everything else on the analytics screens is assembled client-side
 * from `/finance/command-center`, `/finance/retainage-records/register`,
 * `/estimating/analytics/win-loss`, `/compliance/board` and
 * `/inbox/triage-summary`, which already return the shapes the charts need.
 *
 * Types mirror `apps/api/src/modules/finance/finance-analytics.types.ts` and
 * `apps/api/src/modules/field/field-analytics.types.ts`.
 */

export interface MarginTrendSnapshot {
  currencyCode: string | null;
  id: string;
  snapshotNumber: string;
  projectId: string;
  projectNumber: string;
  projectName: string;
  asOfDate: string;
  contractValue: string;
  projectedMargin: string;
  marginPercent: number | null;
}

export interface MarginTrendPoint {
  currencyCode: string | null;
  /** `YYYY-MM`, UTC. */
  period: string;
  projectCount: number;
  /** `null` means no snapshot in that month — a gap, never a zero. */
  contractValue: string | null;
  costToDate: string | null;
  billedToDate: string | null;
  earnedRevenue: string | null;
  overUnderBilling: string | null;
  projectedMargin: string | null;
  marginPercent: number | null;
  snapshots: MarginTrendSnapshot[];
}

export interface MarginTrendResponse {
  generatedAt: string;
  months: number;
  basis: string;
  points: MarginTrendPoint[];
  monthsWithoutSnapshot: number;
  snapshotCount: number;
}

export type BillingPipelineStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'SUBMITTED'
  | 'PAID'
  | 'REJECTED';

export interface BillingPipelineApplication {
  id: string;
  applicationNumber: number;
  projectId: string;
  projectNumber: string;
  projectName: string;
  periodKey: string;
  currentPaymentDue: string;
  updatedAt: string;
}

export interface BillingPipelineStage {
  status: BillingPipelineStatus;
  label: string;
  count: number;
  amount: string;
  applications: BillingPipelineApplication[];
  truncated: boolean;
}

export interface BillingPipelineResponse {
  generatedAt: string;
  basis: string;
  stages: BillingPipelineStage[];
  totals: { count: number; amount: string };
  recordLimitPerStage: number;
}

export type FieldAnalyticsSectionState = 'ok' | 'empty' | 'omitted';
export type FieldAnalyticsSectionId = 'headcount' | 'hours' | 'observations';

export interface FieldAnalyticsSection {
  id: FieldAnalyticsSectionId;
  label: string;
  state: FieldAnalyticsSectionState;
  note: string | null;
  requiredPermission: string | null;
  source: string;
}

export interface FieldHeadcountPoint {
  date: string;
  /** `null` when the dispatch board has no entry for the day. */
  headcount: number | null;
  crews: number | null;
}

export interface FieldHoursByCostCode {
  costCodeId: string | null;
  costCode: string;
  costCodeName: string;
  hours: string;
  entryCount: number;
  trades: string[];
}

export interface FieldObservationBucket {
  key: string;
  label: string;
  count: number;
}

export interface FieldAnalyticsResponse {
  generatedAt: string;
  days: number;
  from: string;
  to: string;
  basis: string;
  sections: FieldAnalyticsSection[];
  headcount: FieldHeadcountPoint[] | null;
  hoursByCostCode: FieldHoursByCostCode[];
  observationsByType: FieldObservationBucket[] | null;
  observationsBySeverity: FieldObservationBucket[] | null;
}

export class AnalyticsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'AnalyticsApiError';
  }
}

function expectData<T>(result: ApiResult<T>, fallback: string): T {
  if (!result.ok || result.data === null) {
    throw new AnalyticsApiError(result.error ?? fallback, result.status);
  }
  return result.data;
}

const FINANCE_ANALYTICS_PATH = `${apiOrigin}/api/v1/finance/analytics`;
const FIELD_ANALYTICS_PATH = `${apiOrigin}/api/v1/field/analytics`;

async function getMarginTrend(months = 12, options?: RequestOptions): Promise<MarginTrendResponse> {
  return expectData(
    await apiClient.get<MarginTrendResponse>(
      `${FINANCE_ANALYTICS_PATH}/margin-trend?months=${months}`,
      options,
    ),
    'The margin trend could not be loaded',
  );
}

async function getBillingPipeline(options?: RequestOptions): Promise<BillingPipelineResponse> {
  return expectData(
    await apiClient.get<BillingPipelineResponse>(`${FINANCE_ANALYTICS_PATH}/billing-pipeline`, options),
    'The billing pipeline could not be loaded',
  );
}

async function getFieldAnalytics(days = 30, options?: RequestOptions): Promise<FieldAnalyticsResponse> {
  return expectData(
    await apiClient.get<FieldAnalyticsResponse>(`${FIELD_ANALYTICS_PATH}/summary?days=${days}`, options),
    'Field analytics could not be loaded',
  );
}

/** Find a field analytics section's meta by id. */
export function findFieldSection(
  sections: readonly FieldAnalyticsSection[],
  id: FieldAnalyticsSectionId,
): FieldAnalyticsSection | undefined {
  return sections.find((section) => section.id === id);
}

export const analyticsClient = { getMarginTrend, getBillingPipeline, getFieldAnalytics };
